import { Injectable } from '@angular/core';

import { DOT_CHART_ICONS, DotChartIcons } from './chart.models';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  private readonly svgCache = new Map<DotChartIcons, string>();
  private readonly canvasCache = new Map<string, HTMLCanvasElement>();

  clearCache(): void {
    this.svgCache.clear();
    this.canvasCache.clear();
  }

  getCachedCanvas(
    fileUrl: DotChartIcons,
    color: string,
    expectedIconSize: number,
  ): HTMLCanvasElement | undefined {
    return this.canvasCache.get(`${fileUrl}:${color}:${expectedIconSize}`);
  }

  async prepareSvgForUse(
    fileUrl: DotChartIcons,
    color: string,
    expectedIconSize: number,
  ): Promise<HTMLCanvasElement> {
    const cacheKey = `${fileUrl}:${color}:${expectedIconSize}`;
    const cached = this.canvasCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const svgIcon = await this.loadSvgFile(fileUrl);
    const coloredSvg = svgIcon.replaceAll('currentColor', color);
    const blob = new Blob([coloredSvg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const dpr = window.devicePixelRatio || 1;

    const img = new Image();

    const canvas = document.createElement('canvas');
    canvas.width = expectedIconSize * dpr;
    canvas.height = expectedIconSize * dpr;
    const ctx = canvas.getContext('2d');

    await this.loadImage(img, url);

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    ctx.scale(dpr, dpr);
    ctx.drawImage(img, 0, 0, expectedIconSize, expectedIconSize);
    URL.revokeObjectURL(url);

    this.canvasCache.set(cacheKey, canvas);
    return canvas;
  }

  labelToIcon(label: string): DotChartIcons {
    let hash = 5381;
    for (let i = 0; i < label.length; i++) {
      hash = (hash * 33) ^ (label.codePointAt(i) ?? 0);
      hash = hash & hash; // 32bit int
    }
    return DOT_CHART_ICONS[Math.abs(hash) % DOT_CHART_ICONS.length];
  }

  private async loadSvgFile(fileUrl: DotChartIcons): Promise<string> {
    if (this.svgCache.has(fileUrl)) {
      return this.svgCache.get(fileUrl) || '';
    }

    const response = await fetch(`assets/charts-icons/${fileUrl}.svg`);
    const svgText = await response.text();
    this.svgCache.set(fileUrl, svgText);

    return svgText;
  }

  private loadImage(img: HTMLImageElement, url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      img.onload = (): void => resolve(void 0);
      img.onerror = (): void => reject(new Error('Image failed to load'));
      img.src = url;
      if (img.complete) {
        resolve(void 0);
      }
    });
  }
}
